import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Bot, ShieldAlert, Sparkles, Wrench, FileCode2, CheckCircle2, Clock3, XCircle, Eye } from "lucide-react";
import api from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const STATUS_META = {
  draft_plan: { label: "Draft Plan", className: "bg-slate-100 text-slate-700" },
  approved_for_build: { label: "Approved", className: "bg-emerald-100 text-emerald-800" },
  needs_review: { label: "Needs Review", className: "bg-amber-100 text-amber-800" },
  rejected: { label: "Rejected", className: "bg-red-100 text-red-700" },
  completed: { label: "Completed", className: "bg-blue-100 text-blue-700" },
};

export default function AIUpgradePage() {
  const { user } = useAuth();
  const isAdmin = user && (user.role === "super_admin" || user.role === "company_admin");
  const [title, setTitle] = useState("");
  const [prompt, setPrompt] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [plan, setPlan] = useState(null);
  const [reviewNote, setReviewNote] = useState("");
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [generatingDraft, setGeneratingDraft] = useState(false);

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/admin/ai-upgrade/requests");
      setHistory(data || []);
      if (!plan && data?.length) {
        setPlan(data[0]);
        setReviewNote(data[0].admin_note || "");
      }
    } catch {
      toast.error("AI request history load failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) loadHistory();
  }, [isAdmin]);

  if (!isAdmin) return <Navigate to="/app" replace />;

  const submitPrompt = async (e) => {
    e.preventDefault();
    if (!prompt.trim()) {
      toast.error("Bangla বা English prompt লিখুন");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/ai-upgrade/plan", {
        title: title.trim() || undefined,
        prompt: prompt.trim(),
      });
      setPlan(data);
      setReviewNote("");
      setPrompt("");
      setTitle("");
      toast.success("AI upgrade plan generated");
      loadHistory();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI plan generation failed");
    } finally {
      setSaving(false);
    }
  };

  const selectPlan = (item) => {
    setPlan(item);
    setReviewNote(item.admin_note || "");
  };

  const updateStatus = async (status) => {
    if (!plan?.id) return;
    setUpdatingStatus(true);
    try {
      const { data } = await api.post(`/admin/ai-upgrade/requests/${plan.id}/status`, {
        status,
        admin_note: reviewNote,
      });
      setPlan(data);
      setHistory((current) => current.map((item) => (item.id === data.id ? data : item)));
      toast.success(`Request marked as ${STATUS_META[status]?.label || status}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Status update failed");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const generateDraftPatch = async () => {
    if (!plan?.id) return;
    setGeneratingDraft(true);
    try {
      const { data } = await api.post(`/admin/ai-upgrade/requests/${plan.id}/generate-draft`, {});
      setPlan(data);
      setHistory((current) => current.map((item) => (item.id === data.id ? data : item)));
      toast.success("Draft patch preview generated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Draft patch generation failed");
    } finally {
      setGeneratingDraft(false);
    }
  };

  const planStatus = STATUS_META[plan?.status] || STATUS_META.draft_plan;

  return (
    <div className="space-y-6" data-testid="ai-upgrade-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-emerald-800 font-semibold">Admin AI</p>
          <h1 className="font-display font-black text-3xl md:text-4xl text-emerald-950 tracking-tight mt-1">AI Upgrade Console</h1>
          <p className="text-sm text-muted-foreground font-body mt-1 max-w-3xl">
            Bangla বা English-এ prompt লিখুন। এই phase-এ AI safe change-plan বানাবে, affected module দেখাবে, risk estimate দেবে, আর validation checklist সাজিয়ে দেবে.
          </p>
        </div>
        <div className="rounded-full bg-amber-100 text-amber-800 px-4 py-2 text-xs font-bold uppercase tracking-wider">
          Safe Mode Only
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1.3fr_0.7fr]">
        <div className="space-y-6">
          <form onSubmit={submitPrompt} className="bg-white rounded-2xl border border-border p-6 space-y-4" data-testid="ai-upgrade-form">
            <div className="flex items-center gap-3 text-emerald-950">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <Bot className="w-5 h-5 text-emerald-800" />
              </div>
              <div>
                <h2 className="font-display font-bold text-xl">Prompt to Plan</h2>
                <p className="text-xs text-muted-foreground">Example: withdrawal approval e OTP add koro, or create a partner performance report.</p>
              </div>
            </div>

            <div>
              <Label>Short Title</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Add OTP for withdrawal approval"
                className="mt-1.5"
                data-testid="ai-upgrade-title"
              />
            </div>

            <div>
              <Label>Admin Prompt</Label>
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={8}
                placeholder="Bangla বা English-এ আপনার change request লিখুন..."
                className="mt-1.5"
                data-testid="ai-upgrade-prompt"
              />
            </div>

            <div className="flex flex-wrap gap-2 text-[11px] text-slate-500">
              <span className="rounded-full bg-slate-100 px-3 py-1">Feature add</span>
              <span className="rounded-full bg-slate-100 px-3 py-1">Bug fix</span>
              <span className="rounded-full bg-slate-100 px-3 py-1">Logic update</span>
              <span className="rounded-full bg-slate-100 px-3 py-1">Risk summary</span>
            </div>

            <Button type="submit" disabled={saving} className="bg-emerald-900 hover:bg-emerald-950 text-white rounded-full" data-testid="ai-upgrade-submit">
              {saving ? "Generating..." : "Generate Upgrade Plan"}
            </Button>
          </form>

          {plan ? (
            <div className="bg-white rounded-2xl border border-border p-6 space-y-5" data-testid="ai-upgrade-plan-result">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-emerald-800 font-semibold">Latest Plan</p>
                  <h2 className="font-display font-black text-2xl text-emerald-950 mt-1">{plan.title}</h2>
                  <p className="text-sm text-muted-foreground mt-1">{plan.summary}</p>
                </div>
                <div className="flex flex-col gap-2 items-end">
                  <div className={"rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider " + (plan.risk_level === "high" ? "bg-red-100 text-red-700" : plan.risk_level === "moderate" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800")}>
                    {plan.risk_level} risk
                  </div>
                  <div className={"rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider " + planStatus.className} data-testid="ai-plan-status">
                    {planStatus.label}
                  </div>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-xl bg-secondary/40 p-4">
                  <div className="flex items-center gap-2 mb-2 text-emerald-950">
                    <Sparkles className="w-4 h-4" />
                    <h3 className="font-semibold">Affected Areas</h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {plan.affected_areas?.map((item) => (
                      <span key={item} className="rounded-full bg-white border border-border px-3 py-1 text-xs font-semibold text-slate-700">{item}</span>
                    ))}
                  </div>
                </div>
                <div className="rounded-xl bg-secondary/40 p-4">
                  <div className="flex items-center gap-2 mb-2 text-emerald-950">
                    <ShieldAlert className="w-4 h-4" />
                    <h3 className="font-semibold">Validation Checks</h3>
                  </div>
                  <ul className="space-y-2 text-sm text-slate-700">
                    {plan.validation_checks?.map((item) => <li key={item}>• {item}</li>)}
                  </ul>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-xl border border-border p-4">
                  <div className="flex items-center gap-2 mb-2 text-emerald-950">
                    <Wrench className="w-4 h-4" />
                    <h3 className="font-semibold">Recommended Steps</h3>
                  </div>
                  <ul className="space-y-2 text-sm text-slate-700">
                    {plan.recommended_steps?.map((item) => <li key={item}>• {item}</li>)}
                  </ul>
                </div>
                <div className="rounded-xl border border-border p-4">
                  <div className="flex items-center gap-2 mb-2 text-emerald-950">
                    <ShieldAlert className="w-4 h-4" />
                    <h3 className="font-semibold">Watchdog Actions</h3>
                  </div>
                  <ul className="space-y-2 text-sm text-slate-700">
                    {plan.watchdog_actions?.map((item) => <li key={item}>• {item}</li>)}
                  </ul>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-xl border border-border p-4">
                  <div className="flex items-center gap-2 mb-2 text-emerald-950">
                    <FileCode2 className="w-4 h-4" />
                    <h3 className="font-semibold">Suggested Files</h3>
                  </div>
                  <ul className="space-y-2 text-sm text-slate-700 font-mono break-all">
                    {plan.suggested_files?.map((item) => <li key={item}>• {item}</li>)}
                  </ul>
                </div>
                <div className="rounded-xl border border-border p-4">
                  <div className="flex items-center gap-2 mb-2 text-emerald-950">
                    <Sparkles className="w-4 h-4" />
                    <h3 className="font-semibold">Implementation Brief</h3>
                  </div>
                  <ul className="space-y-2 text-sm text-slate-700">
                    {plan.implementation_brief?.map((item) => <li key={item}>• {item}</li>)}
                  </ul>
                </div>
              </div>

              <div className="rounded-xl border border-border p-4 space-y-4" data-testid="ai-draft-patch-preview">
                <div className="flex items-center gap-2 text-emerald-950">
                  <FileCode2 className="w-4 h-4" />
                  <h3 className="font-semibold">Draft Patch Preview</h3>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-xs text-slate-500">
                    {plan.draft_patch_generated_at ? `Generated at ${new Date(plan.draft_patch_generated_at).toLocaleString()}` : "No draft patch preview generated yet."}
                  </p>
                  <Button
                    type="button"
                    onClick={generateDraftPatch}
                    disabled={generatingDraft}
                    className="bg-emerald-900 hover:bg-emerald-950 text-white rounded-full"
                    data-testid="ai-generate-draft-button"
                  >
                    {generatingDraft ? "Generating..." : "Generate Draft Patch"}
                  </Button>
                </div>
                <div className="space-y-4">
                  {plan.draft_patch_preview?.length ? plan.draft_patch_preview.map((item) => (
                    <div key={item.file} className="rounded-xl bg-secondary/40 p-4">
                      <p className="font-semibold text-emerald-950 break-all">{item.file}</p>
                      <p className="text-xs text-slate-500 mt-1">{item.purpose}</p>
                      <ul className="mt-3 space-y-2 text-sm text-slate-700">
                        {item.planned_changes?.map((change) => <li key={change}>• {change}</li>)}
                      </ul>
                      <div className="mt-3 rounded-lg bg-slate-950 text-slate-100 p-3 font-mono text-xs space-y-1 overflow-x-auto">
                        {item.pseudo_diff?.map((line) => <div key={line}>{line}</div>)}
                      </div>
                    </div>
                  )) : <p className="text-sm text-slate-500">Generate draft patch preview to inspect planned file-by-file changes.</p>}
                </div>
                <div className="rounded-xl bg-amber-50 border border-amber-200 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-amber-800">Preview Notes</p>
                  <ul className="mt-2 space-y-2 text-sm text-amber-900">
                    {(plan.draft_patch_notes?.length ? plan.draft_patch_notes : ["Draft patch preview has not been generated yet."]).map((note) => <li key={note}>• {note}</li>)}
                  </ul>
                </div>
                <div className="rounded-xl border border-border p-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-emerald-800">Test Preview</p>
                  <div className="mt-3 space-y-3">
                    {plan.test_preview?.length ? plan.test_preview.map((test) => (
                      <div key={test.name} className="rounded-lg bg-secondary/40 p-3">
                        <p className="font-semibold text-emerald-950 text-sm">{test.name}</p>
                        <p className="text-xs text-slate-500 mt-1">{test.scope}</p>
                        <p className="text-sm text-slate-700 mt-2">{test.why}</p>
                      </div>
                    )) : <p className="text-sm text-slate-500">Test preview will appear after draft generation.</p>}
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-border p-4 space-y-4">
                <div className="flex items-center gap-2 text-emerald-950">
                  <ShieldAlert className="w-4 h-4" />
                  <h3 className="font-semibold">Approval Workflow</h3>
                </div>
                <div>
                  <Label>Admin Review Note</Label>
                  <Textarea
                    value={reviewNote}
                    onChange={(e) => setReviewNote(e.target.value)}
                    rows={3}
                    className="mt-1.5"
                    data-testid="ai-upgrade-review-note"
                    placeholder="Write review or deployment notes..."
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" disabled={updatingStatus} onClick={() => updateStatus("approved_for_build")} className="bg-emerald-900 hover:bg-emerald-950 text-white rounded-full" data-testid="ai-approve-button">
                    <CheckCircle2 className="w-4 h-4 mr-2" /> Approve for Build
                  </Button>
                  <Button type="button" disabled={updatingStatus} onClick={() => updateStatus("needs_review")} variant="outline" className="rounded-full" data-testid="ai-review-button">
                    <Eye className="w-4 h-4 mr-2" /> Needs Review
                  </Button>
                  <Button type="button" disabled={updatingStatus} onClick={() => updateStatus("completed")} variant="outline" className="rounded-full" data-testid="ai-complete-button">
                    <Clock3 className="w-4 h-4 mr-2" /> Mark Complete
                  </Button>
                  <Button type="button" disabled={updatingStatus} onClick={() => updateStatus("rejected")} variant="outline" className="rounded-full border-red-200 text-red-700 hover:bg-red-50" data-testid="ai-reject-button">
                    <XCircle className="w-4 h-4 mr-2" /> Reject
                  </Button>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-2xl border border-border p-6" data-testid="ai-upgrade-history">
            <h2 className="font-display font-bold text-xl text-emerald-950">Recent Requests</h2>
            <p className="text-xs text-muted-foreground mt-1">Latest prompt-based upgrade plans from admin.</p>
            <div className="mt-4 space-y-3">
              {loading ? <p className="text-sm text-slate-500">Loading...</p> : null}
              {!loading && history.length === 0 ? <p className="text-sm text-slate-500">No AI upgrade requests yet.</p> : null}
              {history.slice(0, 8).map((item) => (
                <button key={item.id} type="button" onClick={() => selectPlan(item)} className={"w-full text-left rounded-xl border p-4 transition-colors " + (plan?.id === item.id ? "border-emerald-400 bg-emerald-50/50" : "border-border bg-white hover:bg-slate-50")} data-testid={`ai-history-${item.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-emerald-950 text-sm">{item.title}</p>
                      <p className="text-[11px] text-slate-500 mt-1 uppercase tracking-wider">{item.change_type} · {item.language} · {(STATUS_META[item.status] || STATUS_META.draft_plan).label}</p>
                    </div>
                    <span className={"rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider " + (item.risk_level === "high" ? "bg-red-100 text-red-700" : item.risk_level === "moderate" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800")}>
                      {item.risk_level}
                    </span>
                  </div>
                  <p className="text-xs text-slate-600 mt-2 line-clamp-3">{item.prompt}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}